<template>
  <div>
    <!-- 팀 선택 -->
    <div v-if="!selectedTeam" class="select-box" @click="openTeamDropdown">
      <span>{{ teamSearch || '팀 선택' }}</span>
      <span class="arrow">▼</span>
      <div v-if="showTeamDropdown" class="dropdown" @click.stop>
        <input
          v-model="teamSearch"
          placeholder="팀 검색"
          class="dropdown-input"
          @input="onTeamInput"
          @compositionend="onTeamInput"
          ref="teamInput"
        />
        <ul>
          <li v-for="team in availableTeams" :key="team" @mousedown.prevent="selectTeam(team)">
            {{ team }}
          </li>
        </ul>
      </div>
    </div>

    <!-- 팀만 선택, 직책 미선택 -->
    <div v-else-if="selectedTeam && !selectedPosition">
      <div class="selected-line">
        선택한 팀: [{{ selectedTeam }}]
        <button @click="resetTeam" class="reset-btn" title="팀 변경">변경</button>
      </div>
      <div class="select-box" @click="openPositionDropdown">
        <span>{{ positionSearch || '직책/직급 선택' }}</span>
        <span class="arrow">▼</span>
        <div v-if="showPositionDropdown" class="dropdown" @click.stop>
          <input
            v-model="positionSearch"
            placeholder="직책/직급 검색"
            class="dropdown-input"
            @input="onPositionInput"
            @compositionend="onPositionInput"
            ref="positionInput"
          />
          <ul>
            <li v-for="position in availablePositions" :key="position" @mousedown.prevent="selectPosition(position)">
              {{ position }}
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- 팀/직책 모두 선택 -->
    <div v-else>
      <div class="selected-line">
        선택한 팀/직책: [{{ selectedTeam }}/{{ selectedPosition }}]
        <button @click="resetAll" class="reset-btn" title="팀/직책 다시 선택">다시 선택</button>
      </div>
    </div>
  </div>
</template>

<script>
import { ROLES_USER_MAPPING } from '@/utils/constants';
export default {
  name: 'RoleSelect',
  props: {
    value: {
      type: Object,
      default: null
    },
    dropdownOpen: {
      type: Boolean,
      default: false
    }
  },
  emits: ['input', 'role-selected'],
  data() {
    return {
      availableTeams: [],
      availablePositions: [],
      teamSearch: '',
      positionSearch: '',
      showTeamDropdown: false,
      showPositionDropdown: false,
      tempTeam: null // 임시 선택 팀
    }
  },
  computed: {
    selectedTeam() {
      return this.value && this.value.team ? this.value.team : null;
    },
    selectedPosition() {
      return this.value && this.value.position ? this.value.position : null;
    }
  },
  watch: {
    value: {
      handler(val) {
        console.log('RoleSelect value changed:', val);
      },
      immediate: true
    },
    dropdownOpen(val) {
      if (!val) {
        this.showTeamDropdown = false
        this.showPositionDropdown = false
      }
    }
  },
  methods: {
    async fetchTeams(search = '') {
      // ROLES_USER_MAPPING에서 팀 목록 동적 추출
      const allTeams = Array.from(new Set(ROLES_USER_MAPPING.map(r => r.team)));
      if (!search) {
        this.availableTeams = allTeams;
      } else {
        this.availableTeams = allTeams.filter(team => team.includes(search));
      }
    },
    async fetchPositions(team, search = '') {
      // ROLES_USER_MAPPING에서 해당 팀의 직책 목록 동적 추출
      const positions = Array.from(new Set(ROLES_USER_MAPPING.filter(r => r.team === team).map(r => r.position)));
      if (!search) {
        this.availablePositions = positions;
      } else {
        this.availablePositions = positions.filter(position => position.includes(search));
      }
    },
    openTeamDropdown() {
      this.$emit('dropdown-opened')
      this.showTeamDropdown = true
      this.showPositionDropdown = false
      this.fetchTeams(this.teamSearch)
      this.$nextTick(() => {
        if (this.$refs.teamInput) this.$refs.teamInput.focus()
      })
    },
    openPositionDropdown() {
      this.$emit('dropdown-opened')
      this.showPositionDropdown = true
      this.showTeamDropdown = false
      this.fetchPositions(this.selectedTeam, this.positionSearch)
      this.$nextTick(() => {
        if (this.$refs.positionInput) this.$refs.positionInput.focus()
      })
    },
    onTeamInput(e) {
      const value = e && e.target ? e.target.value : this.teamSearch
      this.teamSearch = value
      this.fetchTeams(value)
    },
    onPositionInput(e) {
      const value = e && e.target ? e.target.value : this.positionSearch
      this.positionSearch = value
      this.fetchPositions(this.selectedTeam, value)
    },
    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.showTeamDropdown = false
        this.showPositionDropdown = false
      }
    },
    selectTeam(team) {
      console.log('팀 선택:', team);
      this.tempTeam = team;
      this.teamSearch = '';
      this.positionSearch = '';
      this.availablePositions = [];
      this.$emit('input', { team, position: null });
    },
    resetTeam() {
      this.tempTeam = null;
      this.teamSearch = '';
      this.positionSearch = '';
      this.availablePositions = [];
      this.$emit('input', null);
    },
    selectPosition(position) {
      const team = this.tempTeam || this.selectedTeam;
      console.log('직책 선택:', position, '팀:', team);
      this.positionSearch = '';
      this.showPositionDropdown = false;
      const role = {
        team: team,
        position: position,
        name: `${team} ${position}`,
        id: `${team}-${position}`
      }
      this.$emit('input', role)
      this.$emit('role-selected', role)
      this.tempTeam = null;
    },
    resetAll() {
      this.tempTeam = null;
      this.teamSearch = '';
      this.positionSearch = '';
      this.availablePositions = [];
      this.$emit('input', null);
    }
  },
  mounted() {
    this.fetchTeams()
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
  }
}
</script>

<style scoped>
.select-box {
  width: 100%;
  padding: 4px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  background: #fff;
  margin-bottom: 8px;
  position: relative;
}
.arrow {
  margin-left: 8px;
  font-size: 14px;
}
.dropdown {
  position: absolute;
  left: 0;
  top: 110%;
  width: 100%;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 6px;
  z-index: 20;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  padding: 8px 0;
}
.dropdown-input {
  width: 100%;
  margin: 0 0%;
  margin-bottom: 6px;
  padding: 6px;
  border: 0px solid #eee;
  border-radius: 4px;
}
.dropdown ul {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 140px;
  overflow-y: auto;
}
.dropdown li {
  padding: 8px 16px;
  cursor: pointer;
}
.dropdown li:hover {
  background: #f0f0f0;
}
.selected-line {
  margin: 10px 0;
  font-weight: bold;
}
.reset-btn {
  margin-left: 10px;
  font-size: 12px;
  color: #888;
  background: none;
  border: none;
  cursor: pointer;
}
</style> 